import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import WorkIcon from "@mui/icons-material/Work";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Navbar from "../../Components/Navbar";
import Footer from "../Home/Footer";
import useMetaTags from "../../Hooks/UseMetaTags";
import { getAllProjectsFrontend } from "../../Services/ApiServices";
import ScrollToTopButton from "../../Components/ScrollToTopButton";

const ProjectsSection = () => {
  useMetaTags({
    title: "Showcase Portfolio | Completed Projects",
    description:
      "Explore the completed premium showcase projects by Abilio Realty including structural commercial landmarks, turn-key construction, and luxury residential interior designs.",
    keywords:
      "abilio realty portfolio, showcase projects, completed constructions, home interiors, luxury properties, commercial design",
    ogTitle: "Dynamic Showcase Portfolio | Abilio Realty",
    ogDescription:
      "Discover outstanding architectural feats, design masterworks, and turnkey engineering developments completed by Abilio Realty.",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("All Projects");
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getAllProjectsFrontend();
        if (data && data.status === true) {
          setProjects(data.projects || []);
        } else {
          setError(data?.message || "Failed to load projects.");
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Unable to connect to the server. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handleFilterChange = (event, newValue) => {
    setFilter(newValue);
  };

  const handleOpenProject = (project) => {
    setSelectedProject(project);
    setActiveImageIdx(0);
  };

  const handleCloseProject = () => {
    setSelectedProject(null);
  };

  // Extract images from JSON array safely
  const getProjectImages = (imagesJson) => {
    try {
      const parsed = JSON.parse(imagesJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const filteredProjects = projects.filter((project) => {
    if (filter === "All Projects") return true;
    return project.category === filter;
  });

  const categories = [
    "All Projects",
    "Interior Design",
    "Commercial Construction",
    "Luxury Residential",
    "Real Estate Showcase",
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F9FAFB",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Navbar />

      {/* Hero Header */}
      <Box
        sx={{
          background: "linear-gradient(180deg, #1C1917 0%, #0C0A09 100%)",
          color: "#FFFFFF",
          py: { xs: 8, md: 12 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle geometric overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: "radial-gradient(#FF5722 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#FF5722",
                fontWeight: 700,
                letterSpacing: "4px",
                fontFamily: "Inter",
              }}
            >
              Abilio Realty Masterpieces
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "36px", md: "56px" },
                fontWeight: 800,
                fontFamily: "Manrope",
                lineHeight: 1.2,
                mt: 2,
                mb: 3,
              }}
            >
              Projects Done By Abilio Realty
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: "680px",
                mx: "auto",
                color: "#A8A29E",
                fontSize: { xs: "16px", md: "18px" },
                lineHeight: 1.6,
                fontFamily: "Inter",
              }}
            >
              Step into a gallery of design masterworks, structural landmarks,
              and custom interior transformations delivered with precision and
              architectural excellence.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Filter Tabs Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 6 }}>
          <Tabs
            value={filter}
            onChange={handleFilterChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                color: "#78716C",
                minWidth: "auto",
                mx: 2,
                px: 1,
                "&.Mui-selected": {
                  color: "#FF5722",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#FF5722",
                height: "3px",
                borderRadius: "3px",
              },
            }}
          >
            {categories.map((cat) => (
              <Tab key={cat} label={cat} value={cat} />
            ))}
          </Tabs>
        </Box>

        {/* Dynamic Project Grid */}
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={12}
          >
            <CircularProgress size={50} sx={{ color: "#FF5722" }} />
          </Box>
        ) : error ? (
          <Box py={8} textAlign="center">
            <Typography color="error" variant="h6" fontFamily="Inter">
              {error}
            </Typography>
          </Box>
        ) : filteredProjects.length === 0 ? (
          <Box py={12} textAlign="center">
            <Typography
              variant="h5"
              color="#57534E"
              fontFamily="Manrope"
              fontWeight={500}
              gutterBottom
            >
              No projects available in this category
            </Typography>
            <Typography
              variant="body2"
              color="#78716C"
              fontFamily="Inter"
              mb={4}
            >
              Check back soon as we are constantly completing state-of-the-art
              developments!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {filteredProjects.map((project, idx) => {
              const projectImages = getProjectImages(project.images);
              const coverImg = projectImages[0] || "";

              return (
                <Grid item xs={12} sm={6} md={4} key={project.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <Card
                      onClick={() => handleOpenProject(project)}
                      sx={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        border: "1px solid #E7E5E4",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
                        transition: "transform 0.4s ease, box-shadow 0.4s ease",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 20px 30px rgba(176, 47, 0, 0.08)",
                          borderColor: "#FF5722",
                        },
                      }}
                    >
                      {/* Image Frame with hover scale */}
                      <Box
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          height: 260,
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={coverImg}
                          alt={project.title}
                          sx={{
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform 0.6s ease",
                            "&:hover": {
                              transform: "scale(1.08)",
                            },
                          }}
                        />

                        {/* Category Floating Badge */}
                        <Chip
                          label={project.category}
                          sx={{
                            position: "absolute",
                            top: 16,
                            left: 16,
                            bgcolor: "rgba(255, 87, 34, 0.9)",
                            color: "#FFFFFF",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            fontSize: "11px",
                            height: "26px",
                            backdropFilter: "blur(4px)",
                          }}
                        />
                      </Box>

                      {/* Content Frame */}
                      <CardContent sx={{ p: 3, textAlign: "left" }}>
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: "#1C1917",
                            fontFamily: "Manrope",
                            lineHeight: 1.3,
                            mb: 1.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {project.title}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            gap: 0.5,
                          }}
                        >
                          <LocationOnIcon
                            sx={{ fontSize: "16px", color: "#FF5722" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#78716C",
                              fontFamily: "Inter",
                              fontWeight: 500,
                            }}
                          >
                            {project.location}
                          </Typography>
                        </Box>

                        {/* Brief details tag strip */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 3,
                          }}
                        >
                          {project.area && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AspectRatioIcon
                                sx={{ fontSize: "14px", color: "#A8A29E" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "#78716C", fontFamily: "Inter" }}
                              >
                                {project.area}
                              </Typography>
                            </Box>
                          )}
                          {project.yearCompleted && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <CalendarTodayIcon
                                sx={{ fontSize: "14px", color: "#A8A29E" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "#78716C", fontFamily: "Inter" }}
                              >
                                {project.yearCompleted}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <Button
                          fullWidth
                          variant="outlined"
                          sx={{
                            borderColor: "#FF5722",
                            color: "#FF5722",
                            borderRadius: "8px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            textTransform: "none",
                            py: 1,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: "#FF5722",
                              borderColor: "#FF5722",
                              color: "#FFFFFF",
                            },
                          }}
                        >
                          Discover Project
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Interactive Project Details Modal */}
      <AnimatePresence>
        {selectedProject && (
          <Dialog
            open={!!selectedProject}
            onClose={handleCloseProject}
            maxWidth="lg"
            fullWidth
            scroll="body"
            PaperProps={{
              sx: {
                borderRadius: "24px",
                overflow: "hidden",
                border: "1px solid #E7E5E4",
                boxShadow: "0 24px 60px rgba(0,0,0,0.15)",
                m: { xs: 2, md: 4 },
              },
            }}
          >
            <DialogContent sx={{ p: 0, position: "relative" }}>
              {/* Close Button */}
              <IconButton
                onClick={handleCloseProject}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  zIndex: 10,
                  bgcolor: "rgba(0, 0, 0, 0.6)",
                  color: "#FFFFFF",
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.85)",
                    transform: "rotate(90deg)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <CloseIcon />
              </IconButton>

              <Grid container>
                {/* Visual Showcase - Image Gallery (Left Pane) */}
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      position: "relative",
                      height: { xs: "320px", sm: "400px", md: "550px" },
                      backgroundColor: "#0C0A09",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {/* Active Image */}
                    <Box
                      component="img"
                      src={
                        getProjectImages(selectedProject.images)[activeImageIdx]
                      }
                      alt={selectedProject.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "all 0.5s ease",
                      }}
                    />

                    {/* Left/Right Slideshow Navigation */}
                    {getProjectImages(selectedProject.images).length > 1 && (
                      <>
                        <IconButton
                          onClick={() =>
                            setActiveImageIdx((prev) =>
                              prev === 0
                                ? getProjectImages(selectedProject.images)
                                    .length - 1
                                : prev - 1,
                            )
                          }
                          sx={{
                            position: "absolute",
                            left: 16,
                            bgcolor: "rgba(255, 255, 255, 0.2)",
                            color: "#FFFFFF",
                            backdropFilter: "blur(4px)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.4)" },
                          }}
                        >
                          <ArrowBackIosNewIcon sx={{ fontSize: "18px" }} />
                        </IconButton>

                        <IconButton
                          onClick={() =>
                            setActiveImageIdx((prev) =>
                              prev ===
                              getProjectImages(selectedProject.images).length -
                                1
                                ? 0
                                : prev + 1,
                            )
                          }
                          sx={{
                            position: "absolute",
                            right: 16,
                            bgcolor: "rgba(255, 255, 255, 0.2)",
                            color: "#FFFFFF",
                            backdropFilter: "blur(4px)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.4)" },
                          }}
                        >
                          <ArrowForwardIosIcon sx={{ fontSize: "18px" }} />
                        </IconButton>
                      </>
                    )}

                    {/* Indicator dots inside cover */}
                    {getProjectImages(selectedProject.images).length > 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 20,
                          display: "flex",
                          gap: 1.5,
                          zIndex: 2,
                        }}
                      >
                        {getProjectImages(selectedProject.images).map(
                          (_, idx) => (
                            <Box
                              key={idx}
                              onClick={() => setActiveImageIdx(idx)}
                              sx={{
                                width: activeImageIdx === idx ? "24px" : "8px",
                                height: "8px",
                                borderRadius: "4px",
                                bgcolor:
                                  activeImageIdx === idx
                                    ? "#FF5722"
                                    : "rgba(255, 255, 255, 0.5)",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                              }}
                            />
                          ),
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Thumbnail Row */}
                  {getProjectImages(selectedProject.images).length > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        backgroundColor: "#1C1917",
                        overflowX: "auto",
                        scrollbarWidth: "none",
                      }}
                    >
                      {getProjectImages(selectedProject.images).map(
                        (img, idx) => (
                          <Box
                            key={idx}
                            onClick={() => setActiveImageIdx(idx)}
                            sx={{
                              width: "70px",
                              height: "50px",
                              borderRadius: "6px",
                              overflow: "hidden",
                              border:
                                activeImageIdx === idx
                                  ? "2px solid #FF5722"
                                  : "2px solid transparent",
                              cursor: "pointer",
                              flexShrink: 0,
                              opacity: activeImageIdx === idx ? 1 : 0.6,
                              transition: "all 0.3s ease",
                            }}
                          >
                            <img
                              src={img}
                              alt="thumbnail"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        ),
                      )}
                    </Box>
                  )}
                </Grid>

                {/* Details Description (Right Pane) */}
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{ p: { xs: 4, md: 5 }, flexGrow: 1, textAlign: "left" }}
                  >
                    <Chip
                      label={selectedProject.category}
                      sx={{
                        bgcolor: "rgba(255, 87, 34, 0.1)",
                        color: "#FF5722",
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: "12px",
                        mb: 2,
                      }}
                    />

                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: "24px", md: "32px" },
                        fontWeight: 800,
                        color: "#1C1917",
                        fontFamily: "Manrope",
                        mb: 3,
                        lineHeight: 1.2,
                      }}
                    >
                      {selectedProject.title}
                    </Typography>

                    {/* Metadata Table */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 700,
                        mb: 1,
                        color: "#1C1917",
                      }}
                    >
                      Project Specifications
                    </Typography>
                    <Table
                      size="small"
                      sx={{
                        mb: 4,
                        border: "1px solid #E7E5E4",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontFamily: "Inter",
                              bgcolor: "#F5F5F4",
                            }}
                          >
                            📍 Location
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Inter" }}>
                            {selectedProject.location}
                          </TableCell>
                        </TableRow>
                        {selectedProject.area && (
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontFamily: "Inter",
                                bgcolor: "#F5F5F4",
                              }}
                            >
                              📐 Area
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Inter" }}>
                              {selectedProject.area}
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedProject.yearCompleted && (
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontFamily: "Inter",
                                bgcolor: "#F5F5F4",
                              }}
                            >
                              📅 Completed
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Inter" }}>
                              {selectedProject.yearCompleted}
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedProject.scopeOfWork && (
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontFamily: "Inter",
                                bgcolor: "#F5F5F4",
                              }}
                            >
                              🔨 Scope
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Inter" }}>
                              {selectedProject.scopeOfWork}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>

                    {/* Rich HTML Description */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 700,
                        mb: 1,
                        color: "#1C1917",
                      }}
                    >
                      Project Details
                    </Typography>
                    <Box
                      sx={{
                        color: "#57534E",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        lineHeight: 1.6,
                        "& p": { mb: 2 },
                        maxHeight: "200px",
                        overflowY: "auto",
                        pr: 1,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: selectedProject.description,
                      }}
                    />
                  </Box>

                  {/* Modal Footer CTA */}
                  <Box
                    sx={{
                      p: 3,
                      borderTop: "1px solid #E7E5E4",
                      bgcolor: "#F5F5F4",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        handleCloseProject();
                        window.location.href = `/contact?service=${encodeURIComponent(selectedProject.category)}`;
                      }}
                      sx={{
                        background:
                          "linear-gradient(135deg, #B02F00 0%, #FF5722 100%)",
                        color: "#FFFFFF",
                        borderRadius: "8px",
                        fontFamily: "Inter",
                        fontWeight: 600,
                        py: 1.5,
                        boxShadow: "none",
                        textTransform: "none",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #FF5722 0%, #B02F00 100%)",
                        },
                      }}
                    >
                      Enquire About Similar Project
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>

      <Footer />
      <ScrollToTopButton />
    </Box>
  );
};

export default ProjectsSection;
